/**
 * Performance Settings Component
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the usePerformanceSettings hook.
 */
import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, Save, BookOpen } from 'lucide-react';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';

// Import modular components
import {
  OTIFSettingsTab,
  PPMSettingsTab,
  RatingFormulaTab,
  GradeThresholdsTab,
  PerformanceGuideTab,
  usePerformanceSettings,
  SupplierOTIFData,
} from './performance';

export default function PerformanceSettings() {
  const [activeTab, setActiveTab] = useState('otif');
  const qualityLabel = useQualityMeasureLabel();

  const {
    // State
    loading,
    saving,
    settings,
    hasChanges,
    formData,
    // Threshold state
    editingThreshold,
    thresholdEditData,
    savingThreshold,
    showAddThreshold,
    newThresholdData,
    addingThreshold,
    // Supplier state
    showSupplierOverrides,
    supplierSearch,
    editingSupplier,
    supplierEditData,
    savingSupplier,
    // State setters
    setShowAddThreshold,
    setShowSupplierOverrides,
    setSupplierSearch,
    setSupplierEditData,
    // Handlers
    handleInputChange,
    handleWeightChange,
    handleSave,
    handleResetThresholds,
    handleResetOTIFMappings,
    handleResetPPMMappings,
    handleEditThreshold,
    handleCancelEditThreshold,
    handleSaveThreshold,
    handleThresholdFieldChange,
    handleAddThreshold,
    handleDeleteThreshold,
    handleNewThresholdFieldChange,
    handleEditSupplier,
    handleCancelEditSupplier,
    handleSaveSupplier,
    handleToggleSupplierCustom,
  } = usePerformanceSettings();

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading performance settings...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Supplier Performance Settings</h2>
          <p className="text-sm text-muted-foreground">
            Configure OTIF and {qualityLabel} calculation thresholds, rating formula weights, and score mappings.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!hasChanges || saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="otif">OTIF Settings</TabsTrigger>
          <TabsTrigger value="ppm">{qualityLabel} Settings</TabsTrigger>
          <TabsTrigger value="rating">Rating Formula</TabsTrigger>
          <TabsTrigger value="grades">Grade Thresholds</TabsTrigger>
          <TabsTrigger value="guide" className="flex items-center gap-1">
            <BookOpen className="h-4 w-4" />
            Guide
          </TabsTrigger>
        </TabsList>

        {/* OTIF Settings Tab */}
        <TabsContent value="otif">
          <OTIFSettingsTab
            settings={settings}
            formData={formData}
            showSupplierOverrides={showSupplierOverrides}
            supplierSearch={supplierSearch}
            editingSupplier={editingSupplier}
            supplierEditData={supplierEditData}
            savingSupplier={savingSupplier}
            onInputChange={handleInputChange}
            onResetOTIFMappings={handleResetOTIFMappings}
            onSetShowSupplierOverrides={setShowSupplierOverrides}
            onSetSupplierSearch={setSupplierSearch}
            onSetSupplierEditData={setSupplierEditData}
            onEditSupplier={(supplier) => handleEditSupplier(supplier as SupplierOTIFData)}
            onCancelEditSupplier={handleCancelEditSupplier}
            onSaveSupplier={handleSaveSupplier}
            onToggleSupplierCustom={(supplier) => handleToggleSupplierCustom(supplier as SupplierOTIFData)}
          />
        </TabsContent>

        {/* PPM Settings Tab */}
        <TabsContent value="ppm">
          <PPMSettingsTab
            settings={settings}
            formData={formData}
            onInputChange={handleInputChange}
            onResetPPMMappings={handleResetPPMMappings}
          />
        </TabsContent>

        {/* Rating Formula Tab */}
        <TabsContent value="rating">
          <RatingFormulaTab
            formData={formData}
            onWeightChange={handleWeightChange}
          />
        </TabsContent>

        {/* Grade Thresholds Tab */}
        <TabsContent value="grades">
          <GradeThresholdsTab
            settings={settings}
            editingThreshold={editingThreshold}
            thresholdEditData={thresholdEditData}
            savingThreshold={savingThreshold}
            showAddThreshold={showAddThreshold}
            newThresholdData={newThresholdData}
            addingThreshold={addingThreshold}
            onSetShowAddThreshold={setShowAddThreshold}
            onResetThresholds={handleResetThresholds}
            onEditThreshold={handleEditThreshold}
            onCancelEditThreshold={handleCancelEditThreshold}
            onSaveThreshold={handleSaveThreshold}
            onThresholdFieldChange={handleThresholdFieldChange}
            onAddThreshold={handleAddThreshold}
            onDeleteThreshold={handleDeleteThreshold}
            onNewThresholdFieldChange={handleNewThresholdFieldChange}
          />
        </TabsContent>

        {/* Guide Tab */}
        <TabsContent value="guide">
          <PerformanceGuideTab
            settings={settings}
            formData={formData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
